import { supabase } from "../config/supabase";
import { createFirebaseUser } from './createFirebaseUser';
import { isClientAccessActive } from '../utils/clientAccess';

export interface User {
  userId: string;
  firebase_uid: string;
  userEmail: string;
  userDisplayName: string;
  userGlobalRole?: "superAdmin" | null;
  current_client_id?: string | null;
  userPhoneNumber?: string;
  userPhotoUrl?: string;
  userAssignedCalendarName?: string;
  userUpdatedAt?: string;
  userCreatedAt?: string;
  userRole?: string;
  accessStartAt?: string | null;
  accessEndAt?: string | null;
  agendaColor?: string | null;
}

export interface Client { //Tabela de Tenants/Clients da aplicação
  clientId: string;
  clientName: string;
  clientSlug?: string;
  userRole: string; // userRole definida para o usuário no respectivo Tenant (clientId)
  clientLogoUrl?: string;
  clientLogoWordmarkUrl?: string;
  logo_url?: string;
  logo_wordmark_url?: string;
  accessStartAt?: string | null;
  accessEndAt?: string | null;
}

export class UserService {
  private static readonly USER_CLIENTS_MISSING_COLUMNS_STORAGE_KEY = "user_clients_missing_columns_v1";
  private static readonly USER_CLIENTS_MISSING_ACCESS_WINDOW_MESSAGE =
    "O banco ainda não possui as colunas de vigência de acesso. Aplique a migration 20260511_add_interno_role_and_permissions.sql no Supabase.";
  private static cachedMissingUserClientColumns: Set<string> | null = null;

  private static normalizeEmail(email: string): string {
    return email.trim().toLowerCase();
  }

  private static getKnownMissingUserClientColumns(): Set<string> {
    if (this.cachedMissingUserClientColumns) {
      return new Set(this.cachedMissingUserClientColumns);
    }

    const cached = new Set<string>();

    if (typeof window !== "undefined") {
      try {
        const raw = window.sessionStorage.getItem(this.USER_CLIENTS_MISSING_COLUMNS_STORAGE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) {
            parsed.forEach((item) => {
              if (typeof item === "string" && item.trim()) {
                cached.add(item.trim());
              }
            });
          }
        }
      } catch {
        // ignore local cache read issues
      }
    }

    this.cachedMissingUserClientColumns = cached;
    return new Set(cached);
  }

  private static rememberMissingUserClientColumns(columns: Iterable<string>): void {
    const merged = this.getKnownMissingUserClientColumns();
    let changed = false;

    for (const column of columns) {
      if (!column || merged.has(column)) continue;
      merged.add(column);
      changed = true;
    }

    if (!changed) return;

    this.cachedMissingUserClientColumns = merged;

    if (typeof window !== "undefined") {
      try {
        window.sessionStorage.setItem(
          this.USER_CLIENTS_MISSING_COLUMNS_STORAGE_KEY,
          JSON.stringify([...merged]),
        );
      } catch {
        // ignore local cache write issues
      }
    }
  }

  private static buildUserClientReadSelect(baseFields: string[]): string {
    const missingColumns = this.getKnownMissingUserClientColumns();
    const fields = [...baseFields];

    if (!missingColumns.has("accessStartAt")) {
      fields.push("accessStartAt");
    }
    if (!missingColumns.has("accessEndAt")) {
      fields.push("accessEndAt");
    }
    if (!missingColumns.has("agendaColor")) {
      fields.push("agendaColor");
    }

    return fields.join(",\n        ");
  }

  private static getMissingUserClientColumns(error: any): Set<string> {
    const errorText = [
      error?.message,
      error?.details,
      error?.hint,
      error?.code,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    const missingColumns = new Set<string>();

    if (errorText.includes("accessstartat")) {
      missingColumns.add("accessStartAt");
    }
    if (errorText.includes("accessendat")) {
      missingColumns.add("accessEndAt");
    }
    if (errorText.includes("agendacolor")) {
      missingColumns.add("agendaColor");
    }

    return missingColumns;
  }

  private static async insertUserClientLink(payload: {
    userId: string;
    clientId: string;
    userRole: string;
    accessStartAt?: string | null;
    accessEndAt?: string | null;
    agendaColor?: string | null;
  }): Promise<void> {
    const knownMissingColumns = this.getKnownMissingUserClientColumns();
    const insertPayload: Record<string, string | null> = {
      userId: payload.userId,
      clientId: payload.clientId,
      userRole: payload.userRole,
    };

    if (!knownMissingColumns.has("accessStartAt") && "accessStartAt" in payload) {
      insertPayload.accessStartAt = payload.accessStartAt ?? null;
    }
    if (!knownMissingColumns.has("accessEndAt") && "accessEndAt" in payload) {
      insertPayload.accessEndAt = payload.accessEndAt ?? null;
    }
    if (!knownMissingColumns.has("agendaColor") && "agendaColor" in payload) {
      insertPayload.agendaColor = payload.agendaColor ?? null;
    }

    const { error } = await supabase.from("user_clients").insert(insertPayload);

    const missingColumns = this.getMissingUserClientColumns(error);
    if (missingColumns.size > 0) {
      this.rememberMissingUserClientColumns(missingColumns);
    }

    if (error && missingColumns.size > 0) {
      const legacyPayload: Record<string, string | null> = {
        userId: payload.userId,
        clientId: payload.clientId,
        userRole: payload.userRole,
      };

      if (!missingColumns.has("accessStartAt") && "accessStartAt" in payload) {
        legacyPayload.accessStartAt = payload.accessStartAt ?? null;
      }
      if (!missingColumns.has("accessEndAt") && "accessEndAt" in payload) {
        legacyPayload.accessEndAt = payload.accessEndAt ?? null;
      }
      if (!missingColumns.has("agendaColor") && "agendaColor" in payload) {
        legacyPayload.agendaColor = payload.agendaColor ?? null;
      }

      const { error: legacyError } = await supabase.from("user_clients").insert(legacyPayload);

      if (legacyError) {
        if (legacyError.code === "23505") {
          throw new Error("O usuário já está associado a esta clínica.");
        }
        throw legacyError;
      }

      return;
    }

    if (error) {
      if (error.code === "23505") {
        throw new Error("O usuário já está associado a esta clínica.");
      }
      throw error;
    }
  }

  private static async getSingleUser(
    query: ReturnType<typeof supabase.from<"users", any, any>>,
    lookupLabel: string
  ): Promise<User | null> {
    const { data, error } = await query.limit(2);

    if (error) {
      console.error(`Error fetching user by ${lookupLabel}:`, error);
      throw error;
    }

    const rows = (data ?? []) as User[];
    if (rows.length === 0) {
      return null;
    }

    if (rows.length > 1) {
      throw new Error(`Mais de um usuário encontrado para ${lookupLabel}.`);
    }

    return rows[0] ?? null;
  }

  private static async isUserLinkedToClient(userId: string, clientId: string): Promise<boolean> {
    const { data, error } = await supabase
      .from("user_clients")
      .select("userId")
      .eq("userId", userId)
      .eq("clientId", clientId)
      .maybeSingle();

    if (error) {
      console.error("Error checking user-client link:", error);
      throw error;
    }

    return !!data;
  }

  static async getAllUsers(clientId?: string): Promise<User[]> {
    if (!clientId) {
      console.warn("UserService.getAllUsers chamado sem clientId. Retornando lista vazia por segurança.");
      return [];
    }

    return this.getAllUsersByClientId(clientId);
  }

  static async getUserByFirebaseUid(firebase_uid: string, clientId?: string): Promise<User | null> {
    const normalizedFirebaseUid = firebase_uid.trim();
    const userData = await this.getSingleUser(
      supabase
      .from("users")
      .select("*")
      .eq("firebase_uid", normalizedFirebaseUid),
      `firebase_uid ${normalizedFirebaseUid}`
    );

    if (!userData || !clientId) {
      return userData;
    }

    const isLinked = await this.isUserLinkedToClient(userData.userId, clientId);
    return isLinked ? userData : null;
  }

  static async getUserByEmail(email: string, clientId?: string): Promise<User | null> {
    const normalizedEmail = this.normalizeEmail(email);
    const userData = await this.getSingleUser(
      supabase
      .from("users")
      .select("*")
      .ilike("userEmail", normalizedEmail),
      `email ${normalizedEmail}`
    );

    if (!userData || !clientId) {
      return userData;
    }

    const isLinked = await this.isUserLinkedToClient(userData.userId, clientId);
    return isLinked ? userData : null;
  }

  static async findOrRelinkUserFromFirebaseAuth(
    firebaseUid: string,
    email?: string | null
  ): Promise<User | null> {
    const normalizedFirebaseUid = firebaseUid.trim();
    const userByUid = await this.getUserByFirebaseUid(normalizedFirebaseUid);
    if (userByUid) {
      return userByUid;
    }

    const normalizedEmail = email ? this.normalizeEmail(email) : "";
    if (!normalizedEmail) {
      return null;
    }

    const userByEmail = await this.getUserByEmail(normalizedEmail);
    if (!userByEmail) {
      return null;
    }

    if (
      userByEmail.firebase_uid === normalizedFirebaseUid &&
      userByEmail.userEmail === normalizedEmail
    ) {
      return userByEmail;
    }

    console.warn(
      `[UserService] Relinking Supabase user ${userByEmail.userId} from firebase_uid ${userByEmail.firebase_uid ?? "null"} to ${normalizedFirebaseUid}.`
    );

    const relinkedUser = await this.updateUser(userByEmail.userId, {
      firebase_uid: normalizedFirebaseUid,
      userEmail: normalizedEmail,
    });

    return relinkedUser ?? {
      ...userByEmail,
      firebase_uid: normalizedFirebaseUid,
      userEmail: normalizedEmail,
    };
  }

  static async updateUser(userId: string, updates: Partial<User>): Promise<User | null> {
    console.log("UserService.ts - Tenant selecionado:", updates);
    
    const { userRole, current_client_id, ...userUpdates } = updates;

    if (current_client_id) {
      const isLinked = await this.isUserLinkedToClient(userId, current_client_id);
      if (!isLinked) {
        throw new Error("Usuário não está vinculado ao tenant informado.");
      }
    }

    // Atualiza a role do usuário na tabela user_clients se userRole estiver presente
    if (userRole && current_client_id) {
      await UserService.updateUserRoleForClient(userId, current_client_id, userRole);
    } else if (userRole && !current_client_id) {
      console.warn("Não é possível atualizar os dados do usuário sem um current_client_id.");
      // throw new Error("current_client_id is required to update userRole"); // Comentado para evitar quebrar o fluxo caso o cliente não seja necessário para outras atualizações.
    }

    const { data, error } = await supabase
      .from("users")
      .update({ ...userUpdates, userUpdatedAt: new Date().toISOString() })
      .eq("userId", userId)
      .select()
      .single();

    if (error) {
      console.error("Error updating user:", error);
      throw error;
    }

    return data as User | null;
  }


  static async createUser(
    userData: Partial<User>,
    userRole: string,
    clientId?: string,
    password?: string,
    clientAssignment?: {
      accessStartAt?: string | null;
      accessEndAt?: string | null;
      agendaColor?: string | null;
    }
  ): Promise<{ user: User | null; isNew: boolean }> {
    const normalizedEmail = userData.userEmail ? this.normalizeEmail(userData.userEmail) : "";
    const { firebase_uid } = userData;

    if (!normalizedEmail) {
      throw new Error("O e-mail é obrigatório para criar um usuário.");
    }

    // Prioriza a busca por UID se disponível, senão por e-mail.
    let user = firebase_uid ? await this.getUserByFirebaseUid(firebase_uid) : await this.getUserByEmail(normalizedEmail);

    // Se o usuário já existe no banco da Supabase...
    if (user) {
      // Apenas o vinculamos ao novo tenant se um clientId for fornecido.
      if (clientId) {
          const { data: existingLink, error: linkError } = await supabase
            .from("user_clients")
            .select("*")
            .eq("userId", user.userId)
            .eq("clientId", clientId)
            .maybeSingle();

          if (linkError) throw linkError;

          if (existingLink) {
            throw new Error("Este usuário já está cadastrado nesta clínica.");
          }
          
          await this.insertUserClientLink({
            userId: user.userId,
            clientId: clientId,
            userRole: userRole,
            accessStartAt: clientAssignment?.accessStartAt ?? null,
            accessEndAt: clientAssignment?.accessEndAt ?? null,
            agendaColor: clientAssignment?.agendaColor ?? null,
          });
      }
      return { user, isNew: false };

    } else {
      // Se o usuário não existe no banco da Supabase, precisamos criá-lo.
      let authUid = firebase_uid;

      // Se não temos um UID, significa que o usuário também precisa ser criado na autenticação.
      if (!authUid) {
        if (!password) {
          throw new Error("A senha é obrigatória para criar um novo usuário.");
        }
        authUid = await createFirebaseUser(normalizedEmail, password);
      }

      if (!authUid) {
        throw new Error("Falha ao obter o UID do usuário para criação no banco de dados.");
      }

      const userDataWithUid = {
        ...userData,
        userEmail: normalizedEmail,
        firebase_uid: authUid,
        userCreatedAt: new Date().toISOString(),
        userUpdatedAt: new Date().toISOString(),
      };

      const { data: newUser, error: createError } = await supabase
        .from("users")
        .insert([userDataWithUid])
        .select()
        .single();

      if (createError) {
        console.error("Error creating user in Supabase:", createError);
        throw createError;
      }
      user = newUser as User;
    }

    if (!user) {
        throw new Error("Não foi possível criar ou encontrar o usuário.");
    }

    // Se um clientId for fornecido, cria a associação na tabela 'user_clients'
    if (clientId) {
        // Verificação para evitar duplicatas, embora o fluxo para novos usuários não deva ter uma associação existente.
        const { data: existingLink, error: existingLinkError } = await supabase
          .from("user_clients")
          .select("userId")
          .eq('userId', user.userId)
          .eq('clientId', clientId)
          .maybeSingle();

        if (existingLinkError) {
          throw existingLinkError;
        }

        if (!existingLink) {
            await this.insertUserClientLink({
              userId: user.userId,
              clientId: clientId,
              userRole: userRole,
              accessStartAt: clientAssignment?.accessStartAt ?? null,
              accessEndAt: clientAssignment?.accessEndAt ?? null,
              agendaColor: clientAssignment?.agendaColor ?? null,
            });
        }
    }
    return { user, isNew: true };
  }

  static async getAvailableClients(userId: string): Promise<Client[]> {
    const selectFields = this.buildUserClientReadSelect([
      "clientId",
      "userRole",
      "clients (*)",
    ]);

    let { data, error } = await supabase
      .from('user_clients')
      .select(selectFields)
      .eq('userId', userId);

    const missingColumns = this.getMissingUserClientColumns(error);
    if (missingColumns.size > 0) {
      this.rememberMissingUserClientColumns(missingColumns);
    }

    if (error && missingColumns.size > 0) {
      const legacyQuery = await supabase
        .from('user_clients')
        .select(`
          clientId,
          userRole,
          clients (*)
        `)
        .eq('userId', userId);

      data = legacyQuery.data;
      error = legacyQuery.error;
    }

    if (error) {
      console.error('Error fetching available clients:', error);
      throw error;
    }

    const clients: Client[] = (data ?? [])
      .map((item: any) => ({
        ...item.clients,
        userRole: item.userRole,
        accessStartAt: item.accessStartAt ?? null,
        accessEndAt: item.accessEndAt ?? null,
      }))
      .filter((client) => isClientAccessActive(client.accessStartAt, client.accessEndAt));

    return clients;
  }

  static async setCurrentClient(userId: string, clientId: string): Promise<User | null> {
    return this.updateUser(userId, { current_client_id: clientId });
  }

  static async updateUserRoleForClient(userId: string, clientId: string, userRole: string): Promise<void> {
    await this.updateUserClientAssignment(userId, clientId, { userRole });
  }

  static async updateUserClientAssignment(
    userId: string,
    clientId: string,
    updates: {
      userRole?: string;
      accessStartAt?: string | null;
      accessEndAt?: string | null;
      agendaColor?: string | null;
    }
  ): Promise<void> {
    const knownMissingColumns = this.getKnownMissingUserClientColumns();
    const includesAccessWindowUpdate = "accessStartAt" in updates || "accessEndAt" in updates;

    if (
      includesAccessWindowUpdate &&
      (knownMissingColumns.has("accessStartAt") || knownMissingColumns.has("accessEndAt"))
    ) {
      throw new Error(this.USER_CLIENTS_MISSING_ACCESS_WINDOW_MESSAGE);
    }

    const payload: Record<string, string | null> = {};

    if (typeof updates.userRole === "string") {
      payload.userRole = updates.userRole;
    }
    if ("accessStartAt" in updates && !knownMissingColumns.has("accessStartAt")) {
      payload.accessStartAt = updates.accessStartAt ?? null;
    }
    if ("accessEndAt" in updates && !knownMissingColumns.has("accessEndAt")) {
      payload.accessEndAt = updates.accessEndAt ?? null;
    }
    if ("agendaColor" in updates && !knownMissingColumns.has("agendaColor")) {
      payload.agendaColor = updates.agendaColor ?? null;
    }

    const { error } = await supabase
      .from("user_clients")
      .update(payload)
      .eq("userId", userId)
      .eq("clientId", clientId);

    const missingColumns = this.getMissingUserClientColumns(error);
    if (missingColumns.size > 0) {
      this.rememberMissingUserClientColumns(missingColumns);
    }

    if (error && missingColumns.size > 0) {
      if (
        includesAccessWindowUpdate &&
        (missingColumns.has("accessStartAt") || missingColumns.has("accessEndAt"))
      ) {
        throw new Error(this.USER_CLIENTS_MISSING_ACCESS_WINDOW_MESSAGE);
      }

      const legacyPayload: Record<string, string | null> = {};
      if (typeof updates.userRole === "string") {
        legacyPayload.userRole = updates.userRole;
      }
      if (!missingColumns.has("agendaColor") && "agendaColor" in updates) {
        legacyPayload.agendaColor = updates.agendaColor ?? null;
      }

      if (Object.keys(legacyPayload).length === 0) {
        return;
      }

      const { error: legacyError } = await supabase
        .from("user_clients")
        .update(legacyPayload)
        .eq("userId", userId)
        .eq("clientId", clientId);

      if (legacyError) {
        console.error("Error updating user role for client:", legacyError);
        throw legacyError;
      }

      return;
    }

    if (error) {
      console.error("Error updating user role for client:", error);
      throw error;
    }
  }

  static async setGlobalRole(userId: string, userGlobalRole: "superAdmin" | null): Promise<void> {
    const { error } = await supabase
      .from("users")
      .update({ userGlobalRole, userUpdatedAt: new Date().toISOString() })
      .eq("userId", userId);

    if (error) {
      console.error("Error setting global user role:", error);
      throw error;
    }
  }

  static async getAllUsersByClientId(clientId: string): Promise<User[]> {
    const selectFields = this.buildUserClientReadSelect([
      "userRole",
      "users (*)",
    ]);

    let { data, error } = await supabase
      .from("user_clients")
      .select(selectFields)
      .eq("clientId", clientId);

    const missingColumns = this.getMissingUserClientColumns(error);
    if (missingColumns.size > 0) {
      this.rememberMissingUserClientColumns(missingColumns);
    }

    if (error && missingColumns.size > 0) {
      const legacyQuery = await supabase
        .from("user_clients")
        .select(`
          userRole,
          users (*)
        `)
        .eq("clientId", clientId);

      data = legacyQuery.data;
      error = legacyQuery.error;
    }

    if (error) {
      console.error("Error fetching users by client id:", error);
      throw error;
    }

    const users: User[] = (data ?? []).map((item: any) => ({
      ...item.users,
      userRole: item.userRole,
      accessStartAt: item.accessStartAt ?? null,
      accessEndAt: item.accessEndAt ?? null,
      agendaColor: item.agendaColor ?? null,
    }));

    return users;
  }

  static async deleteUserFromClient(userId: string, clientId: string): Promise<void> {
    const { error } = await supabase
      .from("user_clients")
      .delete()
      .eq("userId", userId)
      .eq("clientId", clientId);

    if (error) {
      console.error("Error deleting user from client:", error);
      throw error;
    }
  }
}
